import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { MediaPlayerInstance } from "@vidstack/react";
import {
  AlignLeft,
  Captions,
  Clapperboard,
  Download as DownloadIcon,
  ExternalLink,
  List,
  ListTree,
  Loader2,
  MessageSquareText,
  Sparkles,
  RefreshCw,
  Square,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  TRANSLATION_LANGUAGES,
  importSourceFor,
  msToSeconds,
  type RecordingStatus,
  type Transcript as TranscriptData,
} from "@rec/shared";
import { ApiError, api } from "@/lib/api";
import { toast } from "@/components/Toaster";
import {
  ChapterList,
  RecordingPlayer,
  seekTo,
  useCaptionSize,
  type TranslatedSubtitles,
} from "@/components/RecordingPlayer";
import { ExportDialog } from "@/components/ExportDialog";
import { TranslateConfirmDialog } from "@/components/TranslateConfirmDialog";
import { InlineRename } from "@/components/InlineRename";
import { VersionsMenu } from "@/components/VersionsMenu";
import { Transcript, useTranscriptLayout } from "@/components/Transcript";
import { ChatPanel } from "@/components/ChatPanel";
import { SummaryPanel } from "@/components/SummaryPanel";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { useServerText } from "@/i18n";
import { useFormat } from "@/i18n/format";

type SidePanel = "chapters" | "transcript" | "chat";

export function Recording() {
  const { recordingId } = useParams({ from: "/r/$recordingId" });
  const search = useSearch({ from: "/r/$recordingId" });
  const navigate = useNavigate();
  const { t } = useTranslation(["recording", "common"]);
  const st = useServerText();
  const localizeSpeakers = useCallback(
    (data: TranscriptData): TranscriptData => ({
      ...data,
      utterances: data.utterances.map((u) => ({ ...u, speakerLabel: st(u.speakerLabel) })),
      speakers: data.speakers.map((sp) => ({ ...sp, label: st(sp.label) })),
    }),
    // `st` changes identity with the language, which re-runs the select.
    [st],
  );
  const f = useFormat();
  const statusLabel = useStatusLabel();

  const queryClient = useQueryClient();
  const playerRef = useRef<MediaPlayerInstance | null>(null);
  const [currentMs, setCurrentMs] = useState(0);
  const [panel, setPanel] = useState<SidePanel>("transcript");
  /** Overrides the "preparing" placeholder for someone who wants it now. */
  const [playAnyway, setPlayAnyway] = useState(false);

  /**
   * The media URL, held still across polls.
   *
   * A progressive recording's `mediaUrl` is presigned fresh on every request,
   * so two polls a second apart return different URLs for the same bytes.
   * Passing that straight to the player changes its `src` on every poll, and
   * the player responds by reloading the media — playback jumps back to zero.
   *
   * This page polls every three seconds while chapters generate and every ten
   * while a transcode runs, and the transcode case is exactly when somebody has
   * chosen to watch the original. So the URL is captured once and only replaced
   * when the *kind* of source changes, which is a real upgrade rather than the
   * same file signed again.
   */
  const [playbackSrc, setPlaybackSrc] = useState<{
    kind: "hls" | "mp4";
    url: string;
  } | null>(null);
  const [layout, setLayout] = useTranscriptLayout();
  const [captionSize, setCaptionSize] = useCaptionSize();
  // Utterances the agent cited, so the transcript can mark them.
  const [citedIds, setCitedIds] = useState<Set<string>>(new Set());
  /** Subtitle language: "original", or a translation code. */
  const [subtitleLang, setSubtitleLang] = useState("original");
  const [exportOpen, setExportOpen] = useState(false);

  const recording = useQuery({
    queryKey: ["recording", recordingId],
    queryFn: () => api.getRecording(recordingId),
    // Chapters are produced in a worker, so the only way this page learns they
    // are done is by asking. Without it the panel sits on "working on it"
    // indefinitely and the feature looks broken.
    refetchInterval: (query) => {
      const current = query.state.data;
      if (!current) return false;
      // The page told people it "updates when it is ready" while only polling
      // for chapters — so a processing recording sat there forever. Anything
      // not finished can still change.
      if (current.status !== "ready" && current.status !== "failed") return 5_000;
      if (current.chaptersStatus === "generating") return 3_000;
      // A ready recording can still be encoding for streaming. Without this the
      // card says "Preparing" until the page is reloaded by hand, which is the
      // same dead end the chapters panel had.
      if (current.transcodeStatus === "running") return 10_000;
      return false;
    },
  });

  const mediaUrl = recording.data?.mediaUrl;
  const mediaType = recording.data?.mediaType;

  useEffect(() => {
    if (!mediaUrl || !mediaType) return;

    setPlaybackSrc((current) => {
      if (!current) return { kind: mediaType, url: mediaUrl };
      // Same kind: the same bytes, signed again. Keep what the player has.
      if (current.kind === mediaType) return current;
      // Somebody chose to watch the original while it encodes. Swapping them
      // onto the finished stream would reset them to zero mid-sentence, which
      // is a worse outcome than staying on the file they asked for. A reload
      // gives them the upgrade.
      if (playAnyway) return current;
      return { kind: mediaType, url: mediaUrl };
    });
  }, [mediaUrl, mediaType, playAnyway]);

  const transcript = useQuery({
    queryKey: ["transcript", recordingId],
    queryFn: () => api.getTranscript(recordingId),
    // Nothing changes on its own once ingested. Speaker corrections do change
    // it, and they invalidate this key explicitly rather than being polled for.
    staleTime: Infinity,
    enabled: recording.data?.status === "ready",
    // "Speaker 1" is written by the server when it transcribes — data, but
    // generated wording rather than a name somebody typed — so it reads
    // "Intervenant 1" in French. Done here so every consumer (transcript,
    // captions, speaker picker) agrees; a real name passes through unchanged.
    select: localizeSpeakers,
  });

  // ── Translated subtitles ───────────────────────────────────────────────────
  const translations = useQuery({
    queryKey: ["translations", recordingId],
    queryFn: () => api.listTranslations(recordingId),
    enabled: (transcript.data?.utterances.length ?? 0) > 0,
    // Live while one is being generated — that is exactly when somebody has
    // just picked a language and is waiting for its subtitles to appear.
    refetchInterval: (query) =>
      (query.state.data?.translations ?? []).some((row) => row.status === "generating")
        ? 4000
        : false,
  });
  const translationRows = translations.data?.translations ?? [];
  const sourceLanguage = translations.data?.sourceLanguage ?? null;

  const requestTranslation = useMutation({
    mutationFn: (language: string) => api.requestTranslation(recordingId, language),
    onSuccess: () => {
      toast(t("toast.translating"), "success");
      void queryClient.invalidateQueries({ queryKey: ["translations", recordingId] });
    },
  });

  const cancelTranslation = useMutation({
    mutationFn: (language: string) => api.cancelTranslation(recordingId, language),
    onSuccess: (_r, language) => {
      toast(t("toast.translationCancelled"), "success");
      if (subtitleLang === language) setSubtitleLang("original");
      void queryClient.invalidateQueries({ queryKey: ["translations", recordingId] });
    },
  });

  const selectedTranslation = translationRows.find(
    (row) => row.language === subtitleLang,
  );
  const translationContent = useQuery({
    queryKey: ["translation", recordingId, subtitleLang],
    queryFn: () => api.getTranslation(recordingId, subtitleLang),
    enabled: subtitleLang !== "original" && selectedTranslation?.status === "ready",
    staleTime: Infinity,
  });

  /**
   * Memoised on the data, not rebuilt per render: this page re-renders once a
   * second during playback (the time readout), and Vidstack's `Track`
   * reconstructs the whole text track whenever `content` changes identity —
   * a fresh Map here meant the captions flickered and re-selected every
   * second while playing.
   */
  const translationData = translationContent.data;
  const activeTranslation: TranslatedSubtitles | undefined = useMemo(() => {
    if (subtitleLang === "original" || translationData?.status !== "ready") {
      return undefined;
    }
    return {
      language: subtitleLang,
      byId: new Map(translationData.utterances.map((u) => [u.utteranceId, u.text])),
    };
  }, [subtitleLang, translationData]);

  /**
   * The whole reading surface follows the language selector: transcript
   * lines and chapters swap their text by id when a ready translation is
   * active, and fall back per item — a chapter regenerated after the
   * translation keeps its original words rather than mislabelling.
   * Timings, ids, speakers, click-to-seek: untouched.
   */
  const displayUtterances = useMemo(() => {
    const source = transcript.data?.utterances ?? [];
    if (!activeTranslation) return source;
    return source.map((u) => ({
      ...u,
      text: activeTranslation.byId.get(u.id) ?? u.text,
    }));
  }, [transcript.data, activeTranslation]);

  const displayChapters = useMemo(() => {
    const source = recording.data?.chapters ?? [];
    if (!activeTranslation || !translationData) return source;
    const byChapter = new Map(
      translationData.chapters.map((c) => [c.chapterId, c]),
    );
    return source.map((chapter) => {
      const translated = byChapter.get(chapter.id);
      return translated
        ? { ...chapter, title: translated.title, summary: translated.summary }
        : chapter;
    });
  }, [recording.data, activeTranslation, translationData]);

  /**
   * Picking an untranslated language asks first — translating spends the
   * workspace's model credit, and a dropdown misclick must not bill anyone.
   */
  const [confirmTranslate, setConfirmTranslate] = useState<string | null>(null);
  const chooseSubtitleLang = (code: string) => {
    if (code === "original") {
      setSubtitleLang(code);
      return;
    }
    const row = translationRows.find((r) => r.language === code);
    if (!row || row.status === "failed") {
      setConfirmTranslate(code);
      return;
    }
    setSubtitleLang(code);
  };

  /**
   * Chapters are generated on request, not at ingest.
   *
   * They run on the workspace's own API key, so the product does not spend it
   * on every recording made — most of which nobody opens.
   */
  const generateChapters = useMutation({
    mutationFn: () => api.generateChapters(recordingId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["recording", recordingId] }),
  });

  /**
   * Naming a speaker and re-attributing a line.
   *
   * Both invalidate the transcript rather than patching it in place: a rename
   * touches every line that speaker said, and the server is the only thing that
   * knows which those are. Search results and chat read the same column, so
   * they follow without anything here telling them to.
   */
  const invalidateTranscript = () =>
    queryClient.invalidateQueries({ queryKey: ["transcript", recordingId] });

  const openInStudio = useMutation({
    mutationFn: () => api.createStudioProject({ sourceRecordingId: recordingId }),
    onSuccess: (studioProject) =>
      void navigate({
        to: "/studio/$projectId",
        params: { projectId: studioProject.id },
      }),
  });

  const cancelChapters = useMutation({
    mutationFn: () => api.cancelChapters(recordingId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["recording", recordingId] }),
  });

  /**
   * Rename the recording.
   *
   * Invalidates the library too: the title belongs to the meeting, so a stale
   * list would show the old name until something else happened to refetch it.
   */
  const renameRecording = useMutation({
    mutationFn: (title: string) => api.renameRecording(recordingId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recording", recordingId] });
      queryClient.invalidateQueries({ queryKey: ["recordings"] });
    },
  });

  /**
   * Build the adaptive ladder for a recording that has none.
   *
   * The recording query already polls while anything is unfinished, so the
   * badge flips to streaming on its own once ffmpeg is done.
   */
  const transcode = useMutation({
    mutationFn: () => api.transcodeRecording(recordingId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["recording", recordingId] }),
  });

  const renameSpeaker = useMutation({
    mutationFn: ({ clusterId, label }: { clusterId: string; label: string }) =>
      api.renameSpeaker(recordingId, clusterId, label),
    onSuccess: invalidateTranscript,
  });

  const reassignUtterance = useMutation({
    mutationFn: ({
      utteranceId,
      target,
    }: {
      utteranceId: string;
      target: { clusterId: string } | { newSpeakerLabel: string };
    }) => api.reassignUtterance(recordingId, utteranceId, target),
    onSuccess: invalidateTranscript,
  });

  /**
   * Recover an upload that has no transcript.
   *
   * Offered only when the recording is playable but explains why it has none —
   * which is exactly the "Partial" state that was previously a dead end.
   */
  const transcribe = useMutation({
    mutationFn: () => api.transcribeRecording(recordingId),
    onSuccess: () => {
      toast(t("toast.transcribing"), "success");
      void queryClient.invalidateQueries({ queryKey: ["recording", recordingId] });
    },
  });

  /**
   * Fetch a failed import again — the owner made the video public, or the
   * platform stopped refusing us. Without this the only way back was deleting
   * the recording and pasting the link again.
   */
  const retryImport = useMutation({
    mutationFn: () => api.retryImport(recordingId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recording", recordingId] });
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
    },
  });

  const stopRecording = useMutation({
    mutationFn: () => api.stopRecording(recordingId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["recording", recordingId] }),
  });

  const handleSeek = useCallback((ms: number) => {
    seekTo(playerRef.current, ms, { play: true });
    // Update immediately rather than waiting for the next timeupdate, so the
    // highlight moves the instant the row is clicked.
    setCurrentMs(ms);
  }, []);

  if (recording.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="aspect-video w-full rounded-xl" />
        <Skeleton className="h-6 w-1/3" />
      </div>
    );
  }

  if (recording.error || !recording.data) {
    // Only claim it is missing when it actually is. Any other failure — a 500,
    // an offline tab — used to read "may have been deleted", sending people to
    // look for a recording that is fine.
    const missing = recording.error instanceof ApiError && recording.error.isNotFound;

    return missing ? (
      <EmptyState
        title={t("notFound.title")}
        description={t("notFound.description")}
      />
    ) : (
      <ErrorState
        title={t("loadError")}
        error={recording.error}
        onRetry={() => void recording.refetch()}
      />
    );
  }

  const data = recording.data;

  if (data.status !== "ready" || !data.mediaUrl) {
    /*
      Say what the provider actually said.
      
      The API derives `statusLabel` and `statusMessage` precisely so a user can
      tell "nobody let the notetaker in" from "the password was wrong". This
      screen threw all of it away and printed "Still processing" — plus "the
      team has been notified", which was not true of anyone.
    */
    return (
      <div className="mx-auto max-w-[560px]">
        <Card className="p-8 text-center">
          <Badge
            tone={
              data.status === "failed"
                ? "danger"
                : data.needsAttention
                  ? "warning"
                  : "accent"
            }
          >
            {/*
              Fall back to the actual status, not to "Processing". Saying
              "Processing" for a `scheduled` recording made this page disagree
              with the library about the same record — one said Scheduled, the
              other said Processing, and neither was wrong about its own data.
            */}
            {data.statusLabel ? st(data.statusLabel) : statusLabel(data.status)}
          </Badge>
          <h1 className="mt-3 text-[16px] font-semibold text-text">{data.title}</h1>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-text-muted">
            {data.statusMessage
              ? st(data.statusMessage)
              : data.failureReason
                ? st(data.failureReason)
                : t("pending.fallback")}
          </p>

          <div className="mt-5 flex justify-center gap-2">
            {/*
              Offered for `scheduled` too, not just `recording`.
              
              A bot only reaches `recording` when a webhook says so, and if
              webhooks are misconfigured it sits at `scheduled` through the whole
              meeting — which is exactly when someone needs this button most. The
              API tells the bot to leave regardless of what status we think it is
              in, so gating the control on our own view of the world was the
              wrong thing to gate on.
            */}
            {data.status === "scheduled" || data.status === "recording" ? (
              <Button
                variant="secondary"
                disabled={stopRecording.isPending}
                onClick={() => stopRecording.mutate()}
              >
                {stopRecording.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Square size={12} fill="currentColor" />
                )}
                {t("pending.stop")}
              </Button>
            ) : null}
            {/* Only an import that never landed: after that it is an upload,
                and a missing transcript has its own Transcribe button. */}
            {data.status === "failed" && importSourceFor(data.platform) && data.sourceUrl ? (
              <>
                <Button
                  variant="secondary"
                  disabled={retryImport.isPending}
                  onClick={() => retryImport.mutate()}
                >
                  {retryImport.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RefreshCw size={13} />
                  )}
                  {t("common:actions.tryAgain")}
                </Button>
                <a
                  href={data.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9.5 items-center gap-1.5 rounded-lg px-3 text-sm text-text-muted hover:bg-surface-hover hover:text-text"
                >
                  <ExternalLink size={13} />
                  {t("pending.openLink")}
                </a>
              </>
            ) : null}
            <Button variant="ghost" onClick={() => window.location.assign("/")}>
              {t("pending.backToLibrary")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,1fr)_460px]">
      <div className="min-w-0 space-y-4">
        {/*
          Do not start pulling the original while segments are being built.

          A progressive MP4 has to be fetched from the front to play anything,
          and on a multi-gigabyte recording that is minutes of stalled spinner
          and a great deal of bandwidth for a file that is about to be replaced
          by segments. Saying what is happening beats a spinner that resolves
          eventually — and the transcript beside this is already usable.

          Still offered, though. A 150 MB upload plays progressively without any
          trouble, and refusing to show somebody their own recording for two
          minutes would be worse than the wait.
        */}
        {data.transcodeStatus === "running" && data.mediaType === "mp4" && !playAnyway ? (
          <div className="flex aspect-video flex-col items-center justify-center gap-3 rounded-xl border border-border bg-bg-subtle p-6 text-center">
            <Loader2 size={22} className="animate-spin text-text-muted" />
            <div>
              <p className="text-[14px] font-medium text-text">
                {t("preparing.title")}
              </p>
              <p className="mx-auto mt-1 max-w-md text-[13px] leading-relaxed text-text-muted">
                {t("preparing.body")}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPlayAnyway(true)}>
              {t("preparing.playAnyway")}
            </Button>
          </div>
        ) : (
          <RecordingPlayer
            src={playbackSrc?.url ?? data.mediaUrl}
            mediaType={playbackSrc?.kind ?? data.mediaType}
            title={data.title}
            chapters={displayChapters}
            // Subtitles are derived from these at display time — no file, no
            // job, and a speaker correction shows up in them immediately.
            utterances={transcript.data?.utterances}
            translation={activeTranslation}
            captionSize={captionSize}
            startAt={search.t ? msToSeconds(search.t) : undefined}
            onTimeChange={setCurrentMs}
            playerRef={playerRef}
          />
        )}

        <div>
          <h1 className="text-lg font-semibold tracking-tight text-text">
            <InlineRename
              value={data.title}
              label={t("renameLabel")}
              onRename={(title) => renameRecording.mutate(title)}
              disabled={renameRecording.isPending}
              inputClassName="w-full max-w-xl"
            />
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-text-muted">
            <span>
              {f.dateTime(data.startedAt)}
            </span>
            {data.durationMs ? (
              <span className="tabular-nums">{f.duration(data.durationMs)}</span>
            ) : null}
            {/*
              Only when there is something to subtitle. Offering a size control
              for captions that cannot exist is a control that does nothing.
            */}
            {(transcript.data?.utterances.length ?? 0) > 0 ? (
              <span className="inline-flex items-center gap-1">
                <Captions size={13} strokeWidth={2} className="text-text-faint" />
                <span className="sr-only">{t("captions.sizeLabel")}</span>
                {(["s", "m", "l"] as const).map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setCaptionSize(size)}
                    aria-pressed={captionSize === size}
                    title={t(`captions.sizeTitle.${size}`)}
                    className={cn(
                      "rounded px-1.5 py-0.5 text-[11.5px] font-medium uppercase transition-colors",
                      captionSize === size
                        ? "bg-surface-hover text-text"
                        : "text-text-faint hover:text-text",
                    )}
                  >
                    {t(`captions.sizeLetter.${size}`)}
                  </button>
                ))}
                {/* Subtitle language. Picking an untranslated language starts
                    the translation on the workspace's model and switches over
                    when it lands. */}
                <select
                  value={subtitleLang}
                  onChange={(e) => chooseSubtitleLang(e.target.value)}
                  title={t("captions.languageTitle")}
                  className="ml-1 rounded-md border border-border bg-bg-subtle px-1.5 py-0.5 text-[11.5px] text-text-muted focus:border-accent focus:outline-none"
                >
                  <option value="original">{t("captions.original")}</option>
                  {/* Grouped, because 93 languages as one flat run is a wall:
                      what exists comes first, the rest wait under Translate. */}
                  {translationRows.length > 0 ? (
                    <optgroup label={t("captions.translations")}>
                      {translationRows.map((row) => {
                        const label = f.languageName(row.language);
                        return (
                          <option key={row.language} value={row.language}>
                            {row.status === "generating"
                              ? t("captions.optionTranslating", { language: label })
                              : row.status === "failed"
                                ? t("captions.optionFailed", { language: label })
                                : label}
                          </option>
                        );
                      })}
                    </optgroup>
                  ) : null}
                  <optgroup label={t("captions.translateTo")}>
                    {TRANSLATION_LANGUAGES.filter(
                      (l) =>
                        l.code !== sourceLanguage &&
                        !translationRows.some((r) => r.language === l.code),
                    ).map(({ code }) => (
                      <option key={code} value={code}>
                        {f.languageName(code)}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {selectedTranslation?.status === "generating" ? (
                  <button
                    type="button"
                    disabled={cancelTranslation.isPending}
                    onClick={() => cancelTranslation.mutate(subtitleLang)}
                    title={t("captions.cancelTitle")}
                    className="rounded px-1.5 py-0.5 text-[11.5px] text-danger hover:bg-danger-subtle"
                  >
                    {t("captions.cancel")}
                  </button>
                ) : null}
              </span>
            ) : null}
            {data.participants.length > 0 ? (
              <span>
                {t("participants", { count: data.participants.length })}
              </span>
            ) : null}
            {data.status === "ready" ? (
              <button
                type="button"
                disabled={openInStudio.isPending}
                onClick={() => openInStudio.mutate()}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-text-faint transition-colors hover:bg-surface-hover hover:text-text"
                title={t("studio.title")}
              >
                {openInStudio.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Clapperboard size={12} strokeWidth={2} />
                )}
                {t("studio.button")}
              </button>
            ) : null}
            {data.status === "ready" && data.mediaUrl ? (
              <button
                type="button"
                onClick={() => setExportOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-text-faint transition-colors hover:bg-surface-hover hover:text-text"
                title={t("export.title")}
              >
                <DownloadIcon size={12} strokeWidth={2} />
                {t("export.button")}
              </button>
            ) : null}
            {data.sourceUrl ? (
              <a
                href={data.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] text-accent hover:underline"
                title={t("importedFromTitle")}
              >
                {importSourceFor(data.platform)
                  ? t("importedFrom", { source: importSourceFor(data.platform)?.label ?? "" })
                  : t("importedFromLink")}
                <ExternalLink size={11} />
              </a>
            ) : null}
            {data.derivedFrom ? (
              <Link
                to="/r/$recordingId"
                params={{ recordingId: data.derivedFrom.id }}
                className="text-[12px] text-accent hover:underline"
                title={t("editedFromTitle")}
              >
                {t("editedFrom", { title: data.derivedFrom.title })}
              </Link>
            ) : null}
            {data.edits.length > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-[12px]">
                {t("edits")}
                {data.edits.slice(0, 3).map((edit) => (
                  <Link
                    key={edit.id}
                    to="/r/$recordingId"
                    params={{ recordingId: edit.id }}
                    className="text-accent hover:underline"
                  >
                    {edit.title}
                  </Link>
                ))}
              </span>
            ) : null}
          </div>

          {data.participants.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {data.participants.map((p) => (
                <Badge key={p.id} tone={p.isHost ? "accent" : "neutral"}>
                  {p.displayName}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>

        {data.failureReason && (transcript.data?.utterances.length ?? 0) === 0 ? (
          <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="min-w-0">
              <p className="text-[13.5px] font-medium text-text">{t("noTranscript.title")}</p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-text-muted">
                {st(data.failureReason)}
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={transcribe.isPending}
              onClick={() => transcribe.mutate()}
            >
              {transcribe.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Sparkles size={13} />
              )}
              {t("noTranscript.transcribe")}
            </Button>
          </Card>
        ) : null}

        {/*
          Progressive playback on a finished recording means the ladder was
          never built — the player downloads the whole file before the first
          frame, and nothing would ever retry on its own.
        */}
        {/* Not while the placeholder above is already saying this. */}
        {data.status === "ready" &&
        data.mediaType === "mp4" &&
        !(data.transcodeStatus === "running" && !playAnyway) ? (
          <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="min-w-0">
              <p className="text-[13.5px] font-medium text-text">
                {data.transcodeStatus === "running"
                  ? t("streaming.preparing")
                  : t("streaming.notPrepared")}
              </p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-text-muted">
                {data.transcodeStatus === "running"
                  ? // A long recording takes minutes and writes nothing until
                    // it is done, so say that rather than leave the page
                    // looking idle.
                    t("streaming.preparingBody")
                  : data.transcodeError
                    ? st(data.transcodeError)
                    : t("streaming.notPreparedBody")}
              </p>
            </div>
            {data.transcodeStatus === "running" ? (
              <span className="flex items-center gap-2 text-[13px] text-text-muted">
                <Loader2 size={13} className="animate-spin" />
                {t("streaming.encoding")}
              </span>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={transcode.isPending}
                onClick={() => transcode.mutate()}
              >
                {transcode.isPending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Sparkles size={13} />
                )}
                {data.transcodeStatus === "failed"
                  ? t("common:actions.tryAgain")
                  : t("streaming.prepare")}
              </Button>
            )}
          </Card>
        ) : null}

        <SummaryPanel
          recordingId={recordingId}
          hasTranscript={(transcript.data?.utterances.length ?? 0) > 0}
          transcriptLoading={transcript.isLoading}
          onSeek={handleSeek}
        />
      </div>

      {/* Side panel. Sticky and independently scrollable so the video stays put
          while the transcript moves. */}
      <Card className="flex h-[calc(100vh-9rem)] flex-col overflow-hidden lg:sticky lg:top-24">
        <div className="flex shrink-0 gap-1 border-b border-border p-1.5">
          <PanelTab
            active={panel === "transcript"}
            onClick={() => setPanel("transcript")}
            icon={<MessageSquareText size={14} strokeWidth={2} />}
            label={t("tabs.transcript")}
          />
          <PanelTab
            active={panel === "chapters"}
            onClick={() => setPanel("chapters")}
            icon={<ListTree size={14} strokeWidth={2} />}
            label={t("tabs.chapters")}
            count={data.chapters.length}
          />
          <PanelTab
            active={panel === "chat"}
            onClick={() => setPanel("chat")}
            icon={<Sparkles size={14} strokeWidth={2} />}
            label={t("tabs.ask")}
          />
        </div>

        <div className="min-h-0 flex-1">
          {panel === "transcript" ? (
            transcript.isLoading ? (
              <div className="space-y-3 p-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : (
              <div className="flex h-full flex-col">
                {/*
                  Utterances are always stored finely — that is what makes
                  click-to-seek land on the sentence you clicked. This only
                  changes how they are drawn, so switching costs nothing.
                */}
                <div className="flex shrink-0 items-center justify-end gap-1 px-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setLayout(layout === "split" ? "grouped" : "split")}
                    title={
                      layout === "split"
                        ? t("layout.mergeTitle")
                        : t("layout.splitTitle")
                    }
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-text-faint transition-colors hover:bg-surface-hover hover:text-text"
                  >
                    {layout === "split" ? (
                      <AlignLeft size={13} strokeWidth={2} />
                    ) : (
                      <List size={13} strokeWidth={2} />
                    )}
                    {layout === "split" ? t("layout.lines") : t("layout.paragraphs")}
                  </button>
                </div>
                <div className="min-h-0 flex-1">
                  <Transcript
                    utterances={displayUtterances}
                    currentMs={currentMs}
                    onSeek={handleSeek}
                    markedIds={citedIds}
                    layout={layout}
                    speakers={transcript.data?.speakers ?? []}
                    onRenameSpeaker={(clusterId, label) =>
                      renameSpeaker.mutate({ clusterId, label })
                    }
                    onReassign={(utteranceId, target) =>
                      reassignUtterance.mutate({ utteranceId, target })
                    }
                  />
                </div>
              </div>
            )
          ) : panel === "chat" ? (
            <ChatPanel
              recordingId={recordingId}
              onSeek={handleSeek}
              // The transcript reads these to mark cited lines, so they are
              // already highlighted when the reader switches tabs. Switching
              // for them would yank the panel out from under the answer.
              onCitationsChange={setCitedIds}
            />
          ) : (
            // No `overflow-y-auto` here any more. The list scrolls itself, the
            // way the transcript does, so it can follow playback and so the
            // Regenerate row stays pinned instead of scrolling away with it.
            <div className="flex h-full flex-col p-2">
              {data.chapters.length > 0 ? (
                <>
                  {/*
                    Regenerating was reachable by API and by nothing else, so a
                    recording chaptered badly — or chaptered before the prompt
                    improved — was stuck with what it got. The summary panel has
                    always offered this; chapters simply never grew the control.

                    The old chapters stay on screen while new ones generate.
                    Blanking the list would take away something usable in
                    exchange for a spinner.
                  */}
                  <div className="flex items-center justify-end gap-2 px-1 pb-2">
                    <ChapterVersions recordingId={recordingId} />
                    {data.chaptersStatus === "generating" ? (
                      <>
                        <span className="flex items-center gap-1.5 text-[12.5px] text-text-muted">
                          <Loader2 size={12} className="animate-spin" />
                          {t("chapters.regenerating")}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={cancelChapters.isPending}
                          onClick={() => cancelChapters.mutate()}
                        >
                          <X size={12} />
                          {t("common:actions.cancel")}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={generateChapters.isPending}
                        onClick={() => generateChapters.mutate()}
                      >
                        <RefreshCw size={12} />
                        {t("chapters.regenerate")}
                      </Button>
                    )}
                  </div>

                  {data.chaptersStatus === "failed" && data.chaptersError ? (
                    // A failed regeneration must not look like the chapters
                    // below it are the new ones.
                    <p className="px-1 pb-2 text-[12.5px] leading-relaxed text-warning">
                      {st(data.chaptersError)}
                    </p>
                  ) : null}

                  <ChapterList
                    chapters={displayChapters}
                    currentMs={currentMs}
                    onSeek={handleSeek}
                  />
                </>
              ) : data.chaptersStatus === "generating" ? (
                <div className="min-h-0 space-y-3 overflow-y-auto p-6 text-center">
                  <p className="flex items-center justify-center gap-2 text-sm text-text-muted">
                    <Loader2 size={14} className="animate-spin" />
                    {t("chapters.dividing")}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={cancelChapters.isPending}
                    onClick={() => cancelChapters.mutate()}
                  >
                    <X size={13} />
                    {t("common:actions.cancel")}
                  </Button>
                </div>
              ) : (
                <div className="min-h-0 overflow-y-auto p-6 text-center">
                  <div className="mb-2 flex justify-end">
                    <ChapterVersions recordingId={recordingId} />
                  </div>
                  {data.chaptersStatus === "failed" ? (
                    <p className="text-sm leading-relaxed text-warning">
                      {data.chaptersError ? st(data.chaptersError) : t("chapters.failed")}
                    </p>
                  ) : (
                    <p className="text-sm leading-relaxed text-text-muted">
                      {t("chapters.empty")}
                    </p>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-3.5"
                    disabled={
                      generateChapters.isPending ||
                      transcript.isLoading ||
                      (transcript.data?.utterances.length ?? 0) === 0
                    }
                    onClick={() => generateChapters.mutate()}
                  >
                    {generateChapters.isPending || transcript.isLoading ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Sparkles size={13} />
                    )}
                    {/*
                      A disabled button with no stated reason is the bug that was
                      reported before. Say which of the two reasons applies.
                    */}
                    {transcript.isLoading
                      ? t("chapters.loadingTranscript")
                      : data.chaptersStatus === "failed"
                        ? t("common:actions.tryAgain")
                        : t("chapters.generate")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {exportOpen ? (
        <ExportDialog
          recordingId={recordingId}
          hasTranscript={(transcript.data?.utterances.length ?? 0) > 0}
          onClose={() => setExportOpen(false)}
        />
      ) : null}

      {confirmTranslate ? (
        <TranslateConfirmDialog
          sourceLanguage={sourceLanguage}
          targetLanguage={confirmTranslate}
          onConfirm={() => {
            setSubtitleLang(confirmTranslate);
            requestTranslation.mutate(confirmTranslate);
            setConfirmTranslate(null);
          }}
          onClose={() => setConfirmTranslate(null)}
        />
      ) : null}
    </div>
  );
}

function PanelTab({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2",
        "text-[13px] font-medium transition-colors",
        active
          ? "bg-bg-subtle text-text"
          : "text-text-muted hover:bg-surface-hover hover:text-text",
      )}
    >
      {icon}
      {label}
      {count !== undefined && count > 0 ? (
        <span className="tabular-nums text-text-faint">{count}</span>
      ) : null}
    </button>
  );
}


/** Chapter version history — every generation and Studio publish lands here. */
function ChapterVersions({ recordingId }: { recordingId: string }) {
  const { t } = useTranslation("recording");
  const st = useServerText();
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);

  const sets = useQuery({
    queryKey: ["chapter-sets", recordingId],
    queryFn: () => api.listChapterSets(recordingId),
    enabled: opened,
  });

  const restore = useMutation({
    mutationFn: (setId: string) => api.restoreChapterSet(recordingId, setId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recording", recordingId] });
      void queryClient.invalidateQueries({ queryKey: ["chapter-sets", recordingId] });
    },
  });

  return (
    <VersionsMenu
      items={(sets.data ?? []).map((set) => ({
        id: set.id,
        label: st(set.label),
        detail: t("chapters.count", { count: set.chapterCount }),
        createdAt: set.createdAt,
        isLive: set.isLive,
      }))}
      loading={sets.isLoading}
      restoringId={restore.isPending ? (restore.variables ?? null) : null}
      onOpen={() => setOpened(true)}
      onRestore={(id) => restore.mutate(id)}
    />
  );
}

/**
 * The status word for the badge when the provider gave nothing more specific.
 * Falls back to "Processing" for a status this build does not know, as the
 * shared label map did.
 */
function useStatusLabel(): (status: RecordingStatus) => string {
  const { t } = useTranslation("common");
  return (status) => {
    switch (status) {
      case "scheduled":
        return t("status.scheduled");
      case "recording":
        return t("status.recording");
      case "ready":
        return t("status.ready");
      case "failed":
        return t("status.failed");
      default:
        return t("status.processing");
    }
  };
}
